<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header-left">
          <div class="panel-icon"><i class="fa fa-map-marker-alt"></i></div>
          <div>
            <div class="panel-title">
              Location-wise Service Report
              <span class="count-badge" v-if="!load && !getLoadingData && reports.length > 0">
                {{ reports.length }} provider{{ reports.length !== 1 ? 's' : '' }}
              </span>
            </div>
            <div class="panel-sub">Enrollment detail grouped by service provider, location and services</div>
          </div>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="filter-bar">
        <adv-search @search="advSearch" :is_service_code="true" :is_date_range="true" :is_service="true"
          :is_status="false" :is_location_enrollment="true" :is_job_report="false" :is_admin_sp_location="true"
          :is_admin_sp="true" :is_payment_status="false" :reportData="excelreports" :titleworksheet="titleworksheet"
          :is_default_tramist_status="'200'" :json_fields="json_fields" :header="header"
          :report_name="'service_report.xls'" :export_inline="true" />
        <div class="tcn-search">
          <div class="tcn-input-wrap">
            <i class="fa fa-search"></i>
            <input type="text" v-model="search_text" placeholder="Search by enrollment no, TCN or name…"
              @keypress.enter="searchByTCN()" />
            <button v-if="search_text" class="tcn-clear" @click="search_text = ''">
              <i class="fa fa-times"></i>
            </button>
          </div>
          <button class="btn-tcn-search" @click="searchByTCN()">
            <i class="fa fa-search"></i> Search
          </button>
        </div>
      </div>

      <!-- Initial prompt -->
      <div class="state-box" v-if="load">
        <i class="fa fa-filter"></i>
        Select the search criteria above and click Search
      </div>

      <!-- Loading -->
      <div class="state-box" v-else-if="getLoadingData">
        <i class="fa fa-spinner fa-spin"></i>
        Generating report…
      </div>

      <!-- Empty -->
      <div class="state-box" v-else-if="reports.length === 0">
        <i class="fa fa-inbox"></i>
        No report data found for the selected criteria
      </div>

      <!-- Report groups -->
      <div class="report-body" v-else>
        <div class="sp-group" v-for="(report, i) in reports" :key="i">

          <!-- SP header -->
          <div class="sp-header">
            <i class="fa fa-building"></i>
            {{ report.sp_name }}
          </div>

          <div class="loc-group" v-for="(loc, j) in report.locations" :key="j">

            <!-- Location header -->
            <div class="loc-header">
              <i class="fa fa-map-pin"></i>
              {{ loc.location_name }}
            </div>

            <div class="svc-group" v-for="(svc, k) in loc.services" :key="k">

              <!-- Service header -->
              <div class="svc-header">
                <i class="fa fa-fingerprint"></i>
                {{ svc.service_name }}
              </div>

              <!-- Enrollment table -->
              <div class="table-wrap">
                <table class="loc-table">
                  <thead>
                    <tr>
                      <th style="width:40px;">#</th>
                      <th style="min-width:160px;">Applicant / TCN</th>
                      <th v-if="svc.service_type === 'fdle'" style="min-width:100px;">ORI</th>
                      <th style="min-width:90px;">Status</th>
                      <th style="min-width:100px;">Enrolled Date</th>
                      <th class="col-amt">Cost</th>
                      <th class="col-amt">Profit</th>
                      <th class="col-amt">Tax</th>
                      <th class="col-amt">Selling Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, idx) in svc.data" :key="idx">
                      <td class="col-num">{{ idx + 1 }}</td>
                      <td>
                        <div class="applicant-name">{{ item.applicant }}</div>
                        <div class="applicant-tcn">{{ item.tcn }}</div>
                      </td>
                      <td v-if="svc.service_type === 'fdle'" class="col-mono">{{ item.ori }}</td>
                      <td>
                        <span class="status-chip" :class="statusClass(item.service_status)">
                          {{ item.service_status || '—' }}
                        </span>
                      </td>
                      <td class="col-date">{{ item.enroll_date || '—' }}</td>
                      <td class="col-amt">${{ fmt(item.cost) }}</td>
                      <td class="col-amt">${{ fmt(item.markup) }}</td>
                      <td class="col-amt">${{ fmt(item.tax) }}</td>
                      <td class="col-amt">${{ fmt(item.selling_price) }}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="subtotal-row">
                      <td :colspan="svc.service_type === 'fdle' ? 5 : 4" class="subtotal-label">Sub Total</td>
                      <td class="col-amt subtotal-val">${{fmt(svc.data.reduce((a, e) => a + Number(e.cost), 0))}}</td>
                      <td class="col-amt subtotal-val">${{fmt(svc.data.reduce((a, e) => a + Number(e.markup), 0))}}
                      </td>
                      <td class="col-amt subtotal-val">${{fmt(svc.data.reduce((a, e) => a + Number(e.tax), 0))}}</td>
                      <td class="col-amt subtotal-val">${{fmt(svc.data.reduce((a, e) => a + Number(e.selling_price),
                        0))}}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

            </div>
          </div>
        </div>

        <!-- Grand total -->
        <div class="grand-total-bar">
          <span class="gt-label">Grand Total</span>
          <div class="gt-cells">
            <div class="gt-cell">
              <div class="gt-cell-label">Cost</div>
              <div class="gt-cell-val">${{ fmtLocale(grandTotalCost) }}</div>
            </div>
            <div class="gt-cell">
              <div class="gt-cell-label">Markup</div>
              <div class="gt-cell-val">${{ fmtLocale(grandTotalMarkup) }}</div>
            </div>
            <div class="gt-cell">
              <div class="gt-cell-label">Tax</div>
              <div class="gt-cell-val">${{ fmtLocale(grandTotalTax) }}</div>
            </div>
            <div class="gt-cell highlight">
              <div class="gt-cell-label">Selling Price</div>
              <div class="gt-cell-val">${{ fmtLocale(grandTotalSelling) }}</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import {
  FETCH_CLIENTS,
  FETCH_SERVICES,
  FETCH_SERVICE_CODES,
  FETCH_LOCATION_WISE_REPORT
} from "@/core/services/store/actions.type";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { mapGetters } from "vuex";
import AdvSearch from "@/includes/search/AdvSearch";

export default {
  components: { AdvSearch },
  computed: {
    ...mapGetters(["currentUser", "getClients", "getServices", "getSearchDate"]),
    grandTotalCost() {
      return this.reports.reduce((a, r) =>
        a + r.locations.reduce((b, l) =>
          b + l.services.reduce((c, s) =>
            c + s.data.reduce((d, e) => d + parseFloat(e.cost || 0), 0), 0), 0), 0);
    },
    grandTotalMarkup() {
      return this.reports.reduce((a, r) =>
        a + r.locations.reduce((b, l) =>
          b + l.services.reduce((c, s) =>
            c + s.data.reduce((d, e) => d + parseFloat(e.markup || 0), 0), 0), 0), 0);
    },
    grandTotalTax() {
      return this.reports.reduce((a, r) =>
        a + r.locations.reduce((b, l) =>
          b + l.services.reduce((c, s) =>
            c + s.data.reduce((d, e) => d + parseFloat(e.tax || 0), 0), 0), 0), 0);
    },
    grandTotalSelling() {
      return this.reports.reduce((a, r) =>
        a + r.locations.reduce((b, l) =>
          b + l.services.reduce((c, s) =>
            c + s.data.reduce((d, e) => d + parseFloat(e.selling_price || 0), 0), 0), 0), 0);
    }
  },
  filters: {
    toFixedTwoDigit(amount) { return parseFloat(amount).toFixed(2); }
  },
  data() {
    return {
      search_text: "",
      search: {
        service: "all",
        company: "all",
        date_from: "",
        date_to: "",
        service_status: "all",
        transmit_status: "200",
        payment_status: "all",
        service_type: "all"
      },
      getLoadingData: false,
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Report", route: "" },
        { id: 3, title: "Service Report", route: "" }
      ],
      reports: [],
      header: [],
      load: true,
      excelreports: [],
      titleworksheet: "",
      json_fields: {
        "": "applicant",
        "  ": "tcn",
        "   ": "ori",
        "    ": "service_status",
        "     ": "enroll_date",
        "      ": "cost",
        "       ": "markup",
        "        ": "tax",
        "         ": "selling_price"
      }
    };
  },
  mounted() {
    this.$store.dispatch(FETCH_CLIENTS);
    this.$store.dispatch(FETCH_SERVICE_CODES);
    this.$store.dispatch(FETCH_SERVICES, this.currentUser.u.is_client ? "self" : "all");
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
  },
  methods: {
    fmt(val) {
      return parseFloat(val || 0).toFixed(2);
    },
    fmtLocale(val) {
      return parseFloat(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    statusClass(status) {
      if (!status || status === '-') return 'chip-neutral';
      const s = String(status).toLowerCase();
      if (s.includes('transmit') || s.includes('final')) return 'chip-green';
      if (s.includes('draft')) return 'chip-amber';
      return 'chip-neutral';
    },
    searchByTCN() {
      if (this.search_text) {
        this.searchRequestHandler({ search_text: this.search_text });
      } else {
        this.$toastr.e("Please enter TCN or name to search");
      }
    },
    advSearch(value) {
      this.search_text = "";
      const { header, search, footer } = value;
      this.header = header;
      this.search = { ...search };
      this.titleworksheet = search.date_from + " - " + search.date_to;
      search.search_text = "";
      search.payment_status = "all";
      this.searchRequestHandler(search, footer);
    },
    mapStatus(item) {
      const s = item.service_status;
      if ((s === '200' || s === '500') && item.service_type !== 'fingerprintcard') return "Ready To Transmit";
      if ((s === '200' || s === '500') && item.service_type === 'fingerprintcard') return "Final";
      if ((s === '300' || s === '600') && item.service_type === 'fingerprintcard') return "Final";
      if ((s === '300' || s === '600') && item.service_type !== 'fingerprintcard') return "Transmitted";
      if (s === '100' || s === '400') return "Draft";
      return "-";
    },
    searchRequestHandler(search, footer) {
      this.getLoadingData = true;
      this.load = false;
      this.$store.dispatch(FETCH_LOCATION_WISE_REPORT, search)
        .then(res => {
          this.getLoadingData = false;
          this.reports = res.data;
          const tempArray = [];
          res.data.forEach(element => {
            element.locations.forEach(element1 => {
              element1.services.forEach(element2 => {
                const totalCost = element2.data.reduce((a, o) => a + Number(o.cost), 0);
                const totalMarkup = element2.data.reduce((a, o) => a + Number(o.markup), 0);
                const totalTax = element2.data.reduce((a, o) => a + Number(o.tax), 0);
                const totalSellingPrice = element2.data.reduce((a, o) => a + Number(o.selling_price), 0);
                const header = `<td colspan=7>${element.sp_name}\n${element1.location_name}\n${element2.service_name}</td>`;
                tempArray.push(
                  { applicant: header, ori: "", service_status: "", enroll_date: "", cost: "", markup: "", tax: "", selling_price: "" },
                  { applicant: "Applicant Name", tcn: "TCN", ori: "ORI", service_status: "Status", enroll_date: "Enrolled Date", cost: "Cost", markup: "Markup", tax: "Tax", selling_price: "Selling Price" },
                  ...element2.data.map(e => {
                    e.service_status = this.mapStatus(e);
                    e.enroll_date = e.enroll_date ? new Date(e.enroll_date).toLocaleDateString() : "";
                    if (e.service_type !== 'fdle') e.ori = "";
                    return e;
                  }),
                  { applicant: "Sub Total", ori: "", service_status: "", enroll_date: "", cost: totalCost, markup: totalMarkup, tax: totalTax, selling_price: totalSellingPrice },
                  { applicant: "", ori: "", service_status: "", enroll_date: "", cost: "", markup: "", tax: "", selling_price: "" }
                );
              });
            });
          });
          this.excelreports = [...tempArray];
          const data = [...res.data];
          if (data.length) {
            this.excelreports.push({
              applicant: "Grand Total", ori: "", service_status: "", enroll_date: "",
              cost: data.reduce((a, r) => a + r.locations.reduce((b, l) => b + l.services.reduce((c, s) => c + s.data.reduce((d, e) => d + parseFloat(e.cost), 0), 0), 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
              markup: data.reduce((a, r) => a + r.locations.reduce((b, l) => b + l.services.reduce((c, s) => c + s.data.reduce((d, e) => d + parseFloat(e.markup), 0), 0), 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
              tax: data.reduce((a, r) => a + r.locations.reduce((b, l) => b + l.services.reduce((c, s) => c + s.data.reduce((d, e) => d + parseFloat(e.tax), 0), 0), 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
              selling_price: data.reduce((a, r) => a + r.locations.reduce((b, l) => b + l.services.reduce((c, s) => c + s.data.reduce((d, e) => d + parseFloat(e.selling_price), 0), 0), 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            });
          }
          if (footer) {
            this.excelreports.push({ applicant: "", ori: "", service_status: "", enroll_date: "", cost: footer[0], markup: footer[1], tax: footer[2], selling_price: footer[3] });
          }
        })
        .catch(err => {
          this.getLoadingData = false;
          this.msg = err.error;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$border: #dde2ec;

.page-wrap {
  padding: 8px 0 80px;
  font-family: $font;
}

/* ── Panel ── */
.panel-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(13, 27, 62, 0.07);
  overflow: visible;
}

/* ── Header ── */
.panel-header {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  background: #fafbfd;
  border-bottom: 1px solid #eef0f6;
  gap: 12px;
  flex-wrap: wrap;
}

.panel-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.panel-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: $blue;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}

.panel-title {
  font-size: 14px;
  font-weight: 700;
  color: $dark;
  display: flex;
  align-items: center;
  gap: 8px;
}

.count-badge {
  font-size: 11px;
  font-weight: 600;
  color: #7a8499;
  background: #eef0f6;
  border-radius: 10px;
  padding: 1px 7px;
}

.panel-sub {
  font-size: 11px;
  color: #8a94a6;
  margin-top: 2px;
}

/* ── Filter bar ── */
.filter-bar {
  padding: 12px 20px;
  border-bottom: 1px solid #f0f3fa;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ── TCN search ── */
.tcn-search {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.tcn-input-wrap {
  position: relative;
  flex: 1;
  min-width: 240px;
  max-width: 420px;

  i.fa-search {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #a0aab8;
    font-size: 12px;
    pointer-events: none;
  }

  input {
    width: 100%;
    height: 34px;
    border: 1.5px solid $border;
    border-radius: 8px;
    padding: 0 30px 0 30px;
    font-size: 13px;
    font-family: $font;
    color: $dark;
    outline: none;
    transition: border-color 0.2s;

    &::placeholder {
      color: #b0bac9;
    }

    &:focus {
      border-color: $blue;
      box-shadow: 0 0 0 3px rgba(56, 154, 212, 0.1);
    }
  }
}

.tcn-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #a0aab8;
  cursor: pointer;
  font-size: 11px;
  padding: 0;

  &:hover {
    color: $dark;
  }
}

.btn-tcn-search {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1.5px solid $blue;
  background: transparent;
  color: $blue;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;

  i {
    font-size: 11px;
  }

  &:hover {
    background: #eef7fb;
  }
}

/* ── States ── */
.state-box {
  text-align: center;
  padding: 52px 24px;
  color: #a0aab8;
  font-size: 13px;
  font-family: $font;

  i {
    font-size: 28px;
    display: block;
    margin-bottom: 10px;
    opacity: 0.4;
  }
}

/* ── Report body ── */
.report-body {
  padding: 0 0 4px;
  max-height: 65vh;
  overflow-y: auto;
}

/* ── SP group ── */
.sp-group {
  border-bottom: 2px solid #eef0f8;

  &:last-of-type {
    border-bottom: none;
  }
}

.sp-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #0d1b3e;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  font-family: $font;
  letter-spacing: 0.2px;

  i {
    font-size: 12px;
    opacity: 0.7;
  }
}

/* ── Location group ── */
.loc-group {
  border-bottom: 1px solid #eef0f8;

  &:last-child {
    border-bottom: none;
  }
}

.loc-header {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 20px 7px 32px;
  background: #eef6fc;
  color: #185fa5;
  font-size: 12.5px;
  font-weight: 700;
  font-family: $font;
  border-bottom: 1px solid #d5eaf7;

  i {
    font-size: 11px;
  }
}

/* ── Service group ── */
.svc-group {
  padding: 0 0 8px;
  border-bottom: 1px dashed #eef0f8;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
}

.svc-header {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px 20px 6px 48px;
  background: #f8fbff;
  color: #276749;
  font-size: 12px;
  font-weight: 700;
  font-family: $font;
  border-bottom: 1px solid #e8f5f0;

  i {
    font-size: 11px;
  }
}

/* ── Table ── */
.table-wrap {
  overflow-x: auto;
  padding: 0 20px 0 20px;
}

.loc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-family: $font;
  table-layout: auto;
  margin: 8px 0;

  thead tr {
    border-bottom: 1.5px solid #e2e8f0;

    th {
      position: sticky;
      top: 0;
      z-index: 2;
      padding: 7px 10px;
      font-size: 11px;
      font-weight: 700;
      color: #7a8499;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      white-space: nowrap;
      text-align: left;
      background: #fff;
      box-shadow: 0 1px 0 #e2e8f0;

      &.col-amt {
        text-align: right;
      }
    }
  }

  tbody tr {
    border-bottom: 1px solid #f5f7fa;
    transition: background 0.12s;

    &:last-child {
      border-bottom: none;
    }

    &:hover td {
      background: #f8fbff;
    }

    td {
      padding: 7px 10px;
      color: #2d3748;
      vertical-align: middle;
    }
  }

  tfoot tr td {
    padding: 8px 10px;
  }
}

.col-num {
  color: #a0aab8;
  font-size: 12px;
  width: 40px;
}

.col-date {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}

.col-mono {
  font-family: 'Courier New', monospace;
  font-size: 11.5px;
  color: #4a5568;
}

.col-amt {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.applicant-name {
  font-weight: 500;
  color: $dark;
  line-height: 1.3;
}

.applicant-tcn {
  font-size: 11px;
  color: #7a8499;
  font-family: 'Courier New', monospace;
  margin-top: 1px;
}

/* ── Status chips ── */
.status-chip {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.chip-green {
  background: #d1fae5;
  color: #065f46;
}

.chip-amber {
  background: #fef3c7;
  color: #92400e;
}

.chip-neutral {
  background: #f1f5f9;
  color: #64748b;
}

/* ── Subtotal row ── */
.subtotal-row {
  background: #f8fbff;
  border-top: 1.5px solid #e2e8f0;
}

.subtotal-label {
  text-align: right;
  font-weight: 700;
  font-size: 12px;
  color: $dark;
}

.subtotal-val {
  font-weight: 700;
  color: $dark;
}

/* ── Grand total bar ── */
.grand-total-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: $dark;
  border-radius: 0 0 12px 12px;
  gap: 16px;
  flex-wrap: wrap;
}

.gt-label {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

.gt-cells {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.gt-cell {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 7px 14px;
  text-align: right;
  min-width: 110px;

  &.highlight {
    background: $blue;
  }
}

.gt-cell-label {
  font-size: 10px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.gt-cell-val {
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  font-variant-numeric: tabular-nums;
}
</style>
